import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Chip,
  Paper,
  Divider,
  TextField,
  InputAdornment,
  Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FolderIcon from '@mui/icons-material/Folder';
import { FormFieldEditorDto, FormFieldType, FormSectionEditorDto } from '../../types/formEditor';

interface FieldListPanelProps {
  fields: FormFieldEditorDto[];
  sections: FormSectionEditorDto[];
  selectedFieldId?: number;
  selectedSectionId?: number;
  onFieldSelect: (fieldId: number) => void;
  onSectionSelect: (sectionId: number) => void;
  onAddField: () => void;
}

const getFieldTypeIcon = (fieldType: FormFieldType | string) => {
  // Convert string enum to numeric if needed
  const numericFieldType = typeof fieldType === 'string'
    ? FormFieldType[fieldType as keyof typeof FormFieldType]
    : fieldType;

  switch (numericFieldType) {
    case FormFieldType.Bool:
      return <CheckBoxIcon fontSize="small" />;
    case FormFieldType.Dropdown:
      return <ArrowDropDownCircleIcon fontSize="small" />;
    case FormFieldType.CheckboxList:
      return <CheckBoxIcon fontSize="small" />;
    case FormFieldType.Date:
    case FormFieldType.DateTime:
      return <CalendarTodayIcon fontSize="small" />;
    default:
      return <TextFieldsIcon fontSize="small" />;
  }
};

const getFieldTypeName = (fieldType: FormFieldType | string): string => {
  // Convert string enum to numeric if needed
  const numericFieldType = typeof fieldType === 'string'
    ? FormFieldType[fieldType as keyof typeof FormFieldType]
    : fieldType;
  switch (numericFieldType) {
    case FormFieldType.String:
      return 'Text';
    case FormFieldType.Multiline:
      return 'Multiline';
    case FormFieldType.Bool:
      return 'Checkbox';
    case FormFieldType.Dimension:
      return 'Dimension';
    case FormFieldType.Int:
      return 'Integer';
    case FormFieldType.Dropdown:
      return 'Dropdown';
    case FormFieldType.Date:
      return 'Date';
    case FormFieldType.DateTime:
      return 'Date & Time';
    case FormFieldType.CheckboxList:
      return 'Checkbox List';
    case FormFieldType.HorizontalRule:
      return 'Static Text';
    case FormFieldType.TextboxWithCheckbox:
      return 'Text + Checkbox';
    default:
      return 'Unknown';
  }
};

export const FieldListPanel: React.FC<FieldListPanelProps> = ({
  fields,
  sections,
  selectedFieldId,
  selectedSectionId,
  onFieldSelect,
  onSectionSelect,
  onAddField
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Sort fields by DisplayOrder first, then TabOrder
  const sortedFields = [...fields].sort((a, b) => {
    // First sort by DisplayOrder
    const displayOrderDiff = a.displayOrder - b.displayOrder;
    if (displayOrderDiff !== 0) return displayOrderDiff;

    // Then sort by TabOrder (0 at the end)
    const aTabOrder = a.pdfConfig?.tabOrder || 0;
    const bTabOrder = b.pdfConfig?.tabOrder || 0;
    if (aTabOrder === 0 && bTabOrder !== 0) return 1;
    if (bTabOrder === 0 && aTabOrder !== 0) return -1;
    return aTabOrder - bTabOrder;
  });

  // Filter fields based on search term
  const filteredFields = sortedFields.filter(field => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      field.description.toLowerCase().includes(searchLower) ||
      field.code.toLowerCase().includes(searchLower) ||
      getFieldTypeName(field.fieldType).toLowerCase().includes(searchLower)
    );
  });

  return (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="h6">
          Sections & Fields
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {sections.length} sections, {filteredFields.length} fields{searchTerm ? ` (filtered)` : ''}
        </Typography>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAddField}
          fullWidth
          sx={{ mt: 1 }}
        >
          Add Field
        </Button>

        <TextField
          fullWidth
          size="small"
          placeholder="Search fields..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mt: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
        {/* Sections */}
        {sections.map((section) => {
          const isSectionSelected = section.id === selectedSectionId;
          return (
            <React.Fragment key={`section-${section.id}`}>
              <ListItem disablePadding>
                <ListItemButton
                  selected={isSectionSelected}
                  onClick={() => onSectionSelect(section.id)}
                  sx={{
                    py: 1,
                    backgroundColor: '#f5f5f5',
                    '&.Mui-selected': {
                      backgroundColor: '#9c27b0',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#7b1fa2'
                      }
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <FolderIcon fontSize="small" />
                    <ListItemText
                      primary={section.description}
                      secondary={`Section ${section.displayOrder}`}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: 'bold'
                      }}
                      secondaryTypographyProps={{
                        fontSize: '0.75rem',
                        color: isSectionSelected ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                      }}
                    />
                  </Box>
                </ListItemButton>
              </ListItem>
              <Divider />
            </React.Fragment>
          );
        })}

        {/* Fields */}
        {filteredFields.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {searchTerm
                ? `No fields match "${searchTerm}"`
                : 'No fields yet. Click "Add Field" to create fields.'
              }
            </Typography>
          </Box>
        ) : (
          filteredFields.map((field, index) => {
            const isSelected = field.id === selectedFieldId;
            const tabOrder = field.pdfConfig?.tabOrder || 0;

            return (
              <React.Fragment key={field.id}>
                {index > 0 && <Divider />}
                <ListItem disablePadding>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => onFieldSelect(field.id)}
                    sx={{
                      py: 1.5,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText',
                        '&:hover': {
                          backgroundColor: 'primary.main'
                        }
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      {tabOrder > 0 && (
                        <Chip
                          label={tabOrder}
                          size="small"
                          color="primary"
                          sx={{
                            width: 32,
                            height: 24,
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}
                        />
                      )}
                      {tabOrder === 0 && (
                        <Box
                          sx={{
                            width: 32,
                            height: 24,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px dashed #ccc',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            color: '#999'
                          }}
                        >
                          -
                        </Box>
                      )}
                      <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                        {getFieldTypeIcon(field.fieldType)}
                      </Box>
                      <ListItemText
                        primary={field.description || field.code}
                        secondary={
                          <Box component="span" sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Typography component="span" variant="caption">
                              {field.code}
                            </Typography>
                            <Chip
                              label={getFieldTypeName(field.fieldType)}
                              size="small"
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                            {field.isRequired && (
                              <Chip
                                label="Required"
                                size="small"
                                color="error"
                                variant="outlined"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            )}
                          </Box>
                        }
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: isSelected ? 'bold' : 'normal'
                        }}
                        secondaryTypographyProps={{
                          component: 'div'
                        }}
                      />
                    </Box>
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            );
          })
        )}
      </List>
    </Paper>
  );
};
